import { useChatStatus } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { FILE_PREVIEW_ANALYTICS_EVENT, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { DetailCardV2 } from '@newfront-insurance/core-ui';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { ExternalLink, Eye, Loader2 } from 'lucide-react';
import { createContext, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Streamdown, type Components } from 'streamdown';

import { SandboxFileLink } from './sandbox-file-link';
import { useOpenChatWorkspaceFile } from '../../../hooks/use-chat-workspace';
import { preprocessMarkdownCitations } from '../../../lib/preprocess-citations';
import { getSandboxFilename, isSandboxFileHref, preprocessSandboxLinks } from '../../../lib/preprocess-sandbox-links';
import { remarkPlugins, shikiTheme, streamdownPlugins } from '../../../lib/streamdown-config';
import { GENERATE_CITATION_FILE_PREVIEW_URL } from '../../../queries/file-preview';
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrevious,
  InlineCitationSource,
  InlineCitationText,
} from '../../ai-elements';
import { type TableData } from '../../markdown-table/lib';
import { TableWrapper } from '../../markdown-table/table-wrapper';
import { type CarouselApi } from '../../ui/carousel';
import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';
import { getFirstCitationPage, isPdfFilename, openPdfPreviewAtPage } from '../lib/pdf-page-preview';
import {
  buildSandboxFileMap,
  type DataExplorerSourceMetadata,
  getDataExplorerSourceMetadata,
  getAttachedFileSourceMetadata,
  getSourceMetadata,
  type SandboxFile,
  type SourcePart,
  type SourcesMap,
} from '../lib/sources';

interface MarkdownWithCitationsProps {
  children: string;
  sourcesMap: SourcesMap;
  /** All unfiltered sources — used to build sandbox file map for shell-generated files */
  allSourcesMap?: SourcesMap;
  messageUuid?: string;
  className?: string;
  /** Callback when user clicks expand on a table (opens in spreadsheet view) */
  onExpandTable?: (tableData: TableData) => void;
  /** Prefix for analytics event names */
  prefix?: string;
}

/**
 * Citation component rendered from preprocessed <citation> HTML tags.
 * Receives sourceId from the AST node and looks up source data from the map.
 */
interface CitationComponentProps {
  sourceIds: string[];
  messageUuid?: string;
}

const SourcesMapContext = createContext<SourcesMap>(new Map());

function formatSnakeCaseLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface DataExplorerCitationContentProps {
  title: string;
  url: string;
  metadata: DataExplorerSourceMetadata;
}

function getFileUuidFromUrl(url: string): string | undefined {
  try {
    return new URL(url).searchParams.get('fileUuid') ?? undefined;
  } catch {
    return /[?&]fileUuid=([^&#]+)/.exec(url)?.[1];
  }
}

function DataExplorerCitationContent({ title, url, metadata }: DataExplorerCitationContentProps) {
  const { preview, isDownloading } = useDownloadFile({});
  const fileUuid = metadata.entityType.toLowerCase() === 'file' ? getFileUuidFromUrl(url) : undefined;
  const actions = [
    <div key="data-explorer-actions" className="flex items-center gap-1">
      {fileUuid && (
        <button
          type="button"
          className="text-muted-foreground hover:bg-muted hover:text-primary flex size-6 items-center justify-center rounded-sm transition-colors disabled:cursor-progress disabled:opacity-80"
          disabled={isDownloading}
          aria-label={isDownloading ? 'Opening file preview' : 'Preview file'}
          aria-busy={isDownloading}
          onClick={(e) => {
            e.stopPropagation();
            void preview(fileUuid, title);
          }}
        >
          {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-4" />}
        </button>
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open in Navigator"
        className="text-muted-foreground hover:bg-muted hover:text-primary flex size-6 items-center justify-center rounded-sm transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="size-4" />
      </a>
    </div>,
  ];

  return (
    <DetailCardV2
      title={title}
      eyebrowProps={{ text: metadata.entityType }}
      defaultExpanded
      isExpandable={false}
      cardProps={{ cardClassName: 'border-none shadow-none p-0 hover:bg-transparent' }}
      actions={actions}
    >
      {metadata.preview.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-xs">
          {metadata.preview.map(({ key, value }) => (
            <li key={key} className="flex items-baseline gap-3">
              <span className="text-muted-foreground w-32 shrink-0">{formatSnakeCaseLabel(key)}</span>
              <span className="font-medium">{value}</span>
            </li>
          ))}
        </ul>
      )}
    </DetailCardV2>
  );
}

function CitationComponent({ sourceIds, messageUuid }: CitationComponentProps) {
  const sourcesMap = useContext(SourcesMapContext);
  const citedSources = sourceIds
    .map((sourceId) => {
      const source = sourcesMap.get(sourceId);
      return source ? { sourceId, source } : undefined;
    })
    .filter((source): source is { sourceId: string; source: SourcePart } => Boolean(source));
  const { preview } = useDownloadFile({});
  const openWorkspaceFile = useOpenChatWorkspaceFile();
  const [generatePreviewUrl] = useMutation(GENERATE_CITATION_FILE_PREVIEW_URL);
  const [pendingSourceId, setPendingSourceId] = useState<string>();
  const analytics = useAnalytics();
  const [selectedSourceIndex, setSelectedSourceIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!carouselApi) return;

    const updateSelectedSourceIndex = (): void => {
      setSelectedSourceIndex(carouselApi.selectedScrollSnap());
    };

    updateSelectedSourceIndex();
    carouselApi.on('select', updateSelectedSourceIndex);
    carouselApi.on('reInit', updateSelectedSourceIndex);

    return () => {
      carouselApi.off('select', updateSelectedSourceIndex);
      carouselApi.off('reInit', updateSelectedSourceIndex);
    };
  }, [carouselApi]);

  if (citedSources.length === 0) {
    return null;
  }

  const hasMultipleSources = citedSources.length > 1;
  const firstSource = citedSources[0].source;

  return (
    <InlineCitation>
      <InlineCitationText />
      <InlineCitationCard>
        <InlineCitationCardTrigger
          label={firstSource.title}
          additionalSourcesCount={citedSources.length > 1 ? citedSources.length - 1 : undefined}
        />
        <InlineCitationCardBody className={hasMultipleSources ? undefined : 'min-h-0'}>
          <InlineCitationCarousel setApi={setCarouselApi}>
            {hasMultipleSources && (
              <div className="bg-steel-100 flex h-8 items-center justify-between px-2 py-1">
                <div className="flex w-6 items-center gap-0">
                  <InlineCitationCarouselPrevious aria-label="Previous source" />
                  <InlineCitationCarouselNext aria-label="Next source" />
                </div>
                <div className="text-steel-400 text-[12px] leading-4">
                  {selectedSourceIndex + 1}/{citedSources.length}
                </div>
              </div>
            )}
            <InlineCitationCarouselContent className="-ml-0">
              {citedSources.map(({ source, sourceId }) => {
                const metadata = getSourceMetadata(source);
                const attachedFileMetadata = getAttachedFileSourceMetadata(source);
                const dataExplorerMetadata = getDataExplorerSourceMetadata(source);
                const isDataExplorer = !!dataExplorerMetadata;
                const fileUuid = metadata?.fileUuid ?? attachedFileMetadata?.fileUuid ?? undefined;
                // A file in the chat's sandbox workspace (no Files record): opened through a signed link.
                const workspacePath = fileUuid ? undefined : (attachedFileMetadata?.path ?? undefined);
                const url = 'url' in source ? source.url : '';
                const textSnippet = metadata?.textSnippet ?? attachedFileMetadata?.textSnippet;
                const pageNumber = metadata?.pageNumber ?? attachedFileMetadata?.pageNumber;
                const citedPage = getFirstCitationPage(pageNumber);
                const canDeepLinkToPage = citedPage !== undefined && isPdfFilename(source.title);
                const isOpeningPreview = pendingSourceId === sourceId;

                return (
                  <InlineCitationCarouselItem key={sourceId} className={hasMultipleSources ? undefined : 'p-3'}>
                    {isDataExplorer ? (
                      <DataExplorerCitationContent title={source.title} url={url} metadata={dataExplorerMetadata} />
                    ) : (
                      <InlineCitationSource
                        title={source.title}
                        description={textSnippet}
                        metadata={
                          <>
                            {metadata?.fileDate && (
                              <div className="text-steel-400 mt-1 text-[12px] leading-4">
                                Last modified: {new Date(metadata.fileDate).toLocaleDateString()}
                              </div>
                            )}
                            {metadata?.emailFrom && (
                              <div className="text-steel-400 mt-1 text-[12px] leading-4">
                                From: {metadata.emailFrom}
                              </div>
                            )}
                          </>
                        }
                      >
                        {fileUuid || workspacePath ? (
                          <button
                            type="button"
                            data-testid="citation-preview-button"
                            className="text-steel-400 hover:text-steel-500 absolute top-0 right-0 transition-colors"
                            disabled={isOpeningPreview}
                            aria-label={isOpeningPreview ? 'Opening file preview' : 'Preview file'}
                            aria-busy={isOpeningPreview}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!fileUuid) {
                                trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
                                  citedPageNumber: canDeepLinkToPage ? citedPage : undefined,
                                  fileExtension: getFileExtension(source.title),
                                  messageUuid,
                                  source: 'citation_preview',
                                  sourceId,
                                });
                                openWorkspaceFile({
                                  path: workspacePath!,
                                  page: canDeepLinkToPage ? String(citedPage) : undefined,
                                });
                                return;
                              }
                              trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
                                citedPageNumber: canDeepLinkToPage ? citedPage : undefined,
                                fileExtension: getFileExtension(source.title),
                                fileUuid,
                                messageUuid,
                                source: 'citation_preview',
                                sourceId,
                              });
                              setPendingSourceId(sourceId);
                              const fallbackToExistingPreview = async (): Promise<void> => {
                                await preview(fileUuid, source.title);
                              };

                              if (canDeepLinkToPage) {
                                // Keep parity with the event emitted internally by useDownloadFile().preview().
                                void analytics.track({
                                  event: FILE_PREVIEW_ANALYTICS_EVENT,
                                  properties: { fileUuid },
                                });
                                void openPdfPreviewAtPage(
                                  async () => {
                                    const { data, errors } = await generatePreviewUrl({
                                      variables: { input: { fileId: fileUuid } },
                                    });
                                    if (errors?.length) return undefined;
                                    return data?.generateFilePreviewUrl.downloadUrl;
                                  },
                                  citedPage,
                                  {
                                    onBlocked: fallbackToExistingPreview,
                                    onError: fallbackToExistingPreview,
                                  },
                                ).finally(() =>
                                  setPendingSourceId((currentSourceId) =>
                                    currentSourceId === sourceId ? undefined : currentSourceId,
                                  ),
                                );
                              } else {
                                void fallbackToExistingPreview().finally(() =>
                                  setPendingSourceId((currentSourceId) =>
                                    currentSourceId === sourceId ? undefined : currentSourceId,
                                  ),
                                );
                              }
                            }}
                          >
                            {isOpeningPreview ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Eye className="size-4" />
                            )}
                          </button>
                        ) : (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-steel-400 hover:text-steel-500 absolute top-0 right-0 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="size-4" />
                          </a>
                        )}
                        {pageNumber && (
                          <div data-testid="citation-page-number" className="text-steel-400 mt-2 text-[12px] leading-4">
                            Page: {String(pageNumber)}
                          </div>
                        )}
                        {metadata?.sectionTitle && (
                          <div className="text-steel-400 mt-1 text-[12px] leading-4">
                            Section: {String(metadata.sectionTitle)}
                          </div>
                        )}
                      </InlineCitationSource>
                    )}
                  </InlineCitationCarouselItem>
                );
              })}
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
}

/**
 * Create component overrides for markdown with citations.
 * Uses preprocessed <citation> tags (via preprocessMarkdownCitations) that are
 * handled at the AST level, so we only need minimal component overrides.
 */
function createComponents(
  sandboxFileMap: Map<string, SandboxFile>,
  messageUuid?: string,
  onExpandTable?: (tableData: TableData) => void,
  prefix?: string,
): Partial<Components> {
  return {
    // Citation elements created by preprocessMarkdownCitations or output directly by the LLM
    // @ts-expect-error - 'citation' is a custom element not in standard Components
    citation: ({ sourceid, sourceids }: { sourceid?: string; sourceids?: string }) => (
      <CitationComponent
        sourceIds={sourceids ? sourceids.split(',').map(decodeURIComponent) : [sourceid ?? '']}
        messageUuid={messageUuid}
      />
    ),
    a: ({ children, href, ...props }) => {
      if (href && isSandboxFileHref(href)) {
        const filename = getSandboxFilename(href);
        return (
          <SandboxFileLink filename={filename} sandboxFileMap={sandboxFileMap} messageUuid={messageUuid}>
            {children}
          </SandboxFileLink>
        );
      }
      return (
        <a className="text-brand-400 hover:underline" target="_blank" rel="noreferrer" href={href ?? '#'} {...props}>
          {children}
        </a>
      );
    },
    table: ({ children, ...props }) => (
      <TableWrapper onExpandTable={onExpandTable} prefix={prefix} messageUuid={messageUuid} {...props}>
        {children}
      </TableWrapper>
    ),
  };
}

// Allow custom <citation> HTML tags through the sanitizer with their attributes
// Note: must use lowercase 'sourceid' because HTML parsers lowercase all attributes
const allowedTags = { citation: ['sourceid', 'sourceids'] };

function NonMemoizedMarkdownWithCitations({
  children,
  sourcesMap,
  allSourcesMap,
  messageUuid,
  className,
  onExpandTable,
  prefix,
}: MarkdownWithCitationsProps) {
  const status = useChatStatus();
  const isStreaming = status === 'streaming';

  // Pre-process markdown to convert reference-style citation links and sandbox file links
  const processedMarkdown = useMemo(() => preprocessSandboxLinks(preprocessMarkdownCitations(children)), [children]);

  // Build filename -> file data map for sandbox file links (uses all sources including shell)
  const sandboxFileMap = useMemo(() => buildSandboxFileMap(allSourcesMap ?? sourcesMap), [allSourcesMap, sourcesMap]);
  const sandboxFileVersion = useMemo(
    () =>
      Array.from(sandboxFileMap.entries())
        .map(([filename, file]) => `${filename}:${file.fileUuid}`)
        .sort()
        .join('|'),
    [sandboxFileMap],
  );
  // Memoize components to avoid recreation on every render
  const components = useMemo(
    () => createComponents(sandboxFileMap, messageUuid, onExpandTable, prefix),
    [sandboxFileMap, messageUuid, onExpandTable, prefix],
  );

  return (
    <SourcesMapContext.Provider value={sourcesMap}>
      <Streamdown
        key={sandboxFileVersion}
        plugins={streamdownPlugins}
        remarkPlugins={remarkPlugins}
        shikiTheme={shikiTheme}
        allowedTags={allowedTags}
        components={components}
        className={className}
        isAnimating={isStreaming}
        animated={isStreaming}
      >
        {processedMarkdown}
      </Streamdown>
    </SourcesMapContext.Provider>
  );
}

function areSourcesMapsEqual(prev: SourcesMap, next: SourcesMap): boolean {
  if (prev === next) return true;
  if (prev.size !== next.size) return false;

  return Array.from(prev.entries()).every(([sourceId, prevSource]) => {
    const nextSource = next.get(sourceId);
    if (!nextSource) return false;
    const prevUrl = 'url' in prevSource ? prevSource.url : undefined;
    const nextUrl = 'url' in nextSource ? nextSource.url : undefined;
    if (prevUrl !== nextUrl || prevSource.title !== nextSource.title) return false;

    const prevMetadata = getSourceMetadata(prevSource);
    const nextMetadata = getSourceMetadata(nextSource);
    const prevAttachedFileMetadata = getAttachedFileSourceMetadata(prevSource);
    const nextAttachedFileMetadata = getAttachedFileSourceMetadata(nextSource);

    return (
      prevMetadata?.textSnippet === nextMetadata?.textSnippet &&
      prevMetadata?.fileDate === nextMetadata?.fileDate &&
      prevMetadata?.emailFrom === nextMetadata?.emailFrom &&
      prevMetadata?.pageNumber === nextMetadata?.pageNumber &&
      prevMetadata?.fileUuid === nextMetadata?.fileUuid &&
      prevMetadata?.sectionTitle === nextMetadata?.sectionTitle &&
      prevAttachedFileMetadata?.fileUuid === nextAttachedFileMetadata?.fileUuid &&
      prevAttachedFileMetadata?.path === nextAttachedFileMetadata?.path &&
      prevAttachedFileMetadata?.textSnippet === nextAttachedFileMetadata?.textSnippet &&
      prevAttachedFileMetadata?.pageNumber === nextAttachedFileMetadata?.pageNumber
    );
  });
}

export const MarkdownWithCitations = memo(
  NonMemoizedMarkdownWithCitations,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    areSourcesMapsEqual(prevProps.sourcesMap, nextProps.sourcesMap) &&
    prevProps.allSourcesMap === nextProps.allSourcesMap &&
    prevProps.messageUuid === nextProps.messageUuid &&
    prevProps.className === nextProps.className,
);
